<template>
  <!-- Lado a lado enquanto couber, quebrando para baixo quando não couber: é o `auto`
       do v-col que faz isso, sem largura declarada por campo. -->
  <!-- align-start: sem ele as colunas esticam até a altura da mais alta, e o
       `align-items: center` do v-list-item deixaria um campo curto boiando no meio da
       altura de uma imagem. Com o topo alinhado, os rótulos ficam na mesma linha. -->
  <v-row justify="start" align="start">
    <!-- `auto` para TODO campo, sem exceção por tipo: a coluna pede a largura do
         conteúdo e, se não couber no que resta da linha, o flex a manda para a
         seguinte — onde o `max-width: 100%` a limita à faixa inteira.

         É por isso que não há regra especial para markdown nem para imagem: um
         markdown curto fica ao lado dos vizinhos, um longo vai sozinho para a linha
         de baixo, e quem decide é o espaço, não o tipo. -->
    <v-col v-for="property in visibleProperties" :key="property.alias" cols="auto">
      <ontos-view-info :title="property.name">
        <!-- A calculada pelo mesmo desenho dos outros campos: o valor chega pronto do servidor,
             na forma gravada — a seleção calculada vira chip, a data vem formatada. -->
        <template v-for="(value, index) in valuesOf(property)" :key="index">
          <!-- Duas decisões distintas, e por isso dois testes:

               A BARRA só entre valores de texto corrido — chip de seleção, régua de item e
               controles de mídia já delimitam o seu. Ver needsValueSeparator.

               A QUEBRA DE LINHA é espaço, não delimitação: valor longo e mídia vão um por
               linha. Imagem decide pelo TIPO e não pelo comprimento — o valor é uma URL, cujo
               tamanho não diz nada sobre a imagem. -->
          <template v-if="index > 0">
            <span v-if="inline(property) && needsValueSeparator(property)"
                  class="pr-1">&puncsp;|&puncsp;</span>
            <div v-else-if="!inline(property)" class="mb-2"/>
          </template>
          <ontos-property-value :property="property"
                                :value="value"
                                :item="instance"
                                :context="context"
                                :choice-domains="choiceDomains"
                                variant="detail"
                                @preview="$emit('preview', $event)"
                                @link="$emit('link', $event)"/>
        </template>
        <ontos-hidden-count :count="hiddenCount(instance, property)"/>
        <!-- Vazio aparece, com um traço: a ficha mostra o item inteiro (ver `fieldsOf`) -->
        <span v-if="!valuesOf(property).length && !hiddenCount(instance, property)" class="text-medium-emphasis">—</span>
        <!-- O que o item, como está gravado, viola neste campo -->
        <ontos-field-failures :failures="failuresAt(instance, property.alias)"/>
      </ontos-view-info>
    </v-col>
  </v-row>
</template>
<script lang="ts">
import {Component, Vue, Prop, toNative} from 'vue-facing-decorator'
import IdeaProperty from "@/types/IdeaProperty";
import {InstanceContext} from "@/types/InstanceContext";
import ViewInfo from "@/components/shared/ViewInfo.vue";
import PropertyValue from "@/components/live/instance/PropertyValue.vue";
import {fieldVisible} from "@/types/Idea";
import {isMediaType} from "@/utils/mediaTypes";
import {hiddenCount, needsValueSeparator} from "@/utils/propertyDisplay";
import HiddenCount from "@/components/shared/HiddenCount.vue";
import {asArray} from "@/utils/arrayUtils";
import Domain from "@/types/Domain";
import FieldFailures from "@/components/live/instance/FieldFailures.vue";
import {failuresAt} from "@/utils/fieldValidation";

/** Acima disto os valores deixam de caber lado a lado na mesma linha. */
const INLINE_MAX = 100

/**
 * Os campos de UMA instância, como a ficha os mostra.
 *
 * Vive fora do InstanceView porque agora é desenhado em dois papéis: a seção da própria
 * instância e a seção de cada relacionamento de cardinalidade 1 — que, desde que deixou
 * de ser uma tabela de uma linha só, se lê exatamente como a ficha do item relacionado.
 * Era esse o bloco que seria copiado.
 *
 * A ORDEM não é decidida aqui: as propriedades chegam prontas do instanceSections, que é
 * quem sabe que o identificador vem primeiro.
 */
@Component({emits: ['preview', 'link'], 
  name: 'ontos-instance-fields',
  components: {'ontos-view-info': ViewInfo, 'ontos-property-value': PropertyValue, 'ontos-field-failures': FieldFailures,
    'ontos-hidden-count': HiddenCount}
})
class InstanceFields extends Vue {
  //Os mesmos nomes das funções, para o template chamar sem um método de repasse
  needsValueSeparator = needsValueSeparator
  hiddenCount = hiddenCount
  failuresAt = failuresAt
  @Prop({type: Array, required: true}) readonly properties!: IdeaProperty[]
  @Prop({type: Object, required: true}) readonly instance!: any
  @Prop({type: Object, required: true}) readonly context!: InstanceContext
  /** Os domínios do ambiente, para os valores de seleção — só repassados ao PropertyValue. */
  @Prop({type: Array, default: () => []}) readonly choiceDomains!: Domain[]

  /**
   * Quem vê cada campo é decidido por item (`fieldVisible`), e por isso aqui, e não no
   * instanceSections: muda quando o item muda, não quando o design da coleção muda.
   */
  get visibleProperties(): IdeaProperty[] {
    return this.properties.filter(property => fieldVisible(this.instance, property))
  }

  valuesOf(property: IdeaProperty): any[] {
    return asArray(property.relationship
        ? this.instance._rels && this.instance._rels[property.alias]
        : this.instance[property.alias])
  }

  /** Vários valores curtos cabem na mesma linha, separados por barra. */
  inline(property: IdeaProperty): boolean {
    if (isMediaType(property)) return false
    const longest = Math.max(
        ...this.valuesOf(property).map(value =>
            value && value._identifier ? value._identifier.length : String(value).length),
        0)
    return longest <= INLINE_MAX
  }
}

export default toNative(InstanceFields)
</script>
